/**
 * Carcinogen + acute-hazard scoring for the gases the BW Ultra measures, plus the
 * single source of truth for the occupational limits used across the admin (the
 * carcinogen trend, the per-incident exposure table, the overview KPI, and the
 * per-firefighter totals).
 *
 * Framing — what the fire chief sees:
 *
 *   • CARCINOGENIC exposure = TOTAL VOC. A firefighter's cancer risk is driven by
 *     the aromatic products of incomplete combustion. The BW Ultra's PID measures
 *     TOTAL VOC — an unspeciated aromatic load — NOT benzene or any single species,
 *     and we never claim otherwise. Total VOC is used directly as the carcinogen
 *     indicator: its cumulative dose (ppm·min) is the burden, and a peak over a
 *     small screening level flags "aromatics present on scene — decon".
 *
 *   • ACUTE hazards = CO, H₂S, LEL, O₂. These are immediate dangers, not the cancer
 *     signal. CO is acute poisoning (NIOSH REL 35 ppm 8-h TWA; 200 ppm ceiling;
 *     1200 ppm IDLH); H₂S a 10 ppm ceiling; LEL a 10% action level. CO is also a
 *     combustion marker, but it is presented for its ACUTE danger.
 *
 * So the two signals stay distinct everywhere: `carcinogenOver` (VOC) is the cancer
 * signal; `acuteOver` (CO / H₂S / LEL) is immediate danger. We never imply a
 * speciated benzene measurement.
 *
 * Sources:
 *   NIOSH Pocket Guide — Carbon monoxide (REL 35 ppm TWA / 200 ppm ceiling):
 *     https://www.cdc.gov/niosh/npg/npgd0105.html
 *   IARC Monographs vol. 132 — Occupational exposure as a firefighter (Group 1):
 *     https://publications.iarc.who.int/Book-And-Report-Series/Iarc-Monographs-On-The-Identification-Of-Carcinogenic-Hazards-To-Humans/Occupational-Exposure-As-A-Firefighter-2023
 */

import { toNum as num } from "@/lib/format";

/** NIOSH REL for carbon monoxide — 35 ppm 8-h TWA (ceiling 200 ppm). CO is an ACUTE hazard. */
export const CO_NIOSH_REL_PPM = 35;

/**
 * Total-VOC screening level — 1 ppm. The PID measures TOTAL (unspeciated) VOC; a
 * peak over this small level flags an elevated aromatic-carcinogen load present on
 * scene. It is NOT benzene and NOT a speciated measurement — the real carcinogen
 * burden is the cumulative VOC dose (ppm·min), not this screen line.
 */
export const VOC_SCREEN_PPM = 1;

/**
 * NIOSH REL for hydrogen sulfide — 10 ppm (10-minute ceiling). H2S is an acute
 * toxicant; it lives here only so the over-limit screens draw their limits from
 * one place instead of hard-coding them at each call site.
 */
export const H2S_NIOSH_REL_PPM = 10;

/**
 * H₂S is governed by a 10-minute CEILING (10 ppm) — a never-to-exceed limit. So
 * comparing a measured PEAK to it is category-correct (a ceiling is defined on the
 * instantaneous reading). Alias of H2S_NIOSH_REL_PPM, named for that comparison.
 */
export const H2S_CEILING_PPM = H2S_NIOSH_REL_PPM;

/**
 * LEL action level — 10% of the Lower Explosive Limit. Defined on the instantaneous
 * reading, so a measured PEAK ≥ 10% is a correct action-level exceedance.
 */
export const LEL_ACTION_PCT = 10;

/** Occupational limits (ppm) for the gases the BW Ultra measures, keyed by UI label. */
export const NIOSH_REL_PPM = {
  CO: CO_NIOSH_REL_PPM,
  H2S: H2S_NIOSH_REL_PPM,
  VOC: VOC_SCREEN_PPM,
} as const;

export type RelGas = keyof typeof NIOSH_REL_PPM;

/** A measured gas peak as a percentage of its limit. Null if unmeasured. */
export function pctRel(
  gas: RelGas,
  peakPpm: string | number | null | undefined,
): number | null {
  const v = num(peakPpm);
  return v === null ? null : (v / NIOSH_REL_PPM[gas]) * 100;
}

/** Peak CO as a percentage of the NIOSH CO REL (35 ppm). Null if unmeasured. */
export function coPctRel(
  coPeakPpm: string | number | null | undefined,
): number | null {
  return pctRel("CO", coPeakPpm);
}

/**
 * Peak VOC as a percentage of the total-VOC screen (1 ppm). Total PID VOC is
 * unspeciated, so this is an aromatic-carcinogen screen, not a benzene dose.
 * Null if unmeasured.
 */
export function vocPctRel(
  vocPeakPpm: string | number | null | undefined,
): number | null {
  return pctRel("VOC", vocPeakPpm);
}

/**
 * True if any measured gas peak in the record is at or over its limit — the basis
 * for the overview "readings over limit" KPI. Covers the acute gases (CO, H₂S) and
 * the carcinogen marker (VOC) so a dangerous spike of any kind counts.
 */
export function isOverNioshRel(record: {
  co_ppm_peak?: string | number | null;
  h2s_ppm_peak?: string | number | null;
  voc_ppm_peak?: string | number | null;
}): boolean {
  return (
    (pctRel("CO", record.co_ppm_peak) ?? 0) >= 100 ||
    (pctRel("H2S", record.h2s_ppm_peak) ?? 0) >= 100 ||
    (pctRel("VOC", record.voc_ppm_peak) ?? 0) >= 100
  );
}

/**
 * True if this record's CO peak reaches or exceeds the NIOSH CO REL (35 ppm) — an
 * ACUTE carbon-monoxide exceedance on that incident.
 */
export function isCoOverRel(record: {
  co_ppm_peak?: string | number | null;
}): boolean {
  return (coPctRel(record.co_ppm_peak) ?? 0) >= 100;
}

/**
 * True if this record's VOC peak reaches or exceeds the total-VOC screen (1 ppm) —
 * the aromatic-carcinogen indicator. Total PID VOC is unspeciated, so this flags
 * "elevated aromatic load present", not a measured benzene dose. The monthly
 * carcinogen trend counts these.
 */
export function isVocOverRel(record: {
  voc_ppm_peak?: string | number | null;
}): boolean {
  return (vocPctRel(record.voc_ppm_peak) ?? 0) >= 100;
}

/**
 * Per-hazard screening status for one exposure record — deterministic, computed
 * from the measured peak/dose vs the published limits (no rule profile, no model),
 * so it is always available and fully auditable. Two distinct signals:
 *
 *  • `carcinogenOver` — TOTAL VOC over its screen (the cancer signal). Total VOC is
 *    the unspeciated aromatic load; we never claim a benzene measurement.
 *  • `acuteOver` — an immediate danger: CO over its NIOSH REL, H₂S over its 10 ppm
 *    ceiling, or LEL over its 10% action level.
 *
 * CO is judged on the measured incident TWA when the dose engine has populated it
 * (a correct REL exceedance — the REL is an 8-h TWA) and falls back to the peak
 * screen only while `co_twa_ppm` is still null. The UI renders carcinogen vs acute
 * as distinct chips so a CO/H₂S spike is never read as a cancer signal, and vice
 * versa. `reasons` names every tripped limit for a tooltip.
 */
export type ExposureStatus = {
  over: boolean;
  /** TOTAL VOC over its screen — the carcinogen signal. */
  carcinogenOver: boolean;
  /** CO / H₂S / LEL over their limits — immediate danger. */
  acuteOver: boolean;
  /** CO over its NIOSH REL — an ACUTE marker (measured TWA when populated, else peak). */
  coOver: boolean;
  /** TOTAL VOC over its screen — the CARCINOGEN marker. */
  vocOver: boolean;
  reasons: string[];
};

export function exposureStatus(record: {
  co_ppm_peak?: string | number | null;
  h2s_ppm_peak?: string | number | null;
  voc_ppm_peak?: string | number | null;
  lel_pct_peak?: string | number | null;
  co_twa_ppm?: string | number | null;
}): ExposureStatus {
  const reasons: string[] = [];
  let coOver = false;
  let vocOver = false;
  let h2sLelOver = false;

  // CO (ACUTE): prefer the measured incident TWA when the dose engine has populated
  // it — an average CO concentration ≥ the 35 ppm REL is a correct REL exceedance;
  // only when `co_twa_ppm` is null do we fall back to the peak ÷ REL screen.
  const coTwa = num(record.co_twa_ppm);
  if (coTwa !== null) {
    if (coTwa >= CO_NIOSH_REL_PPM) {
      coOver = true;
      reasons.push(
        `CO TWA ${coTwa} ppm ≥ ${CO_NIOSH_REL_PPM} ppm NIOSH REL (acute, 8-h TWA)`,
      );
    }
  } else {
    const co = num(record.co_ppm_peak);
    if (co !== null && co >= CO_NIOSH_REL_PPM) {
      coOver = true;
      reasons.push(
        `CO peak ${co} ppm ≥ ${CO_NIOSH_REL_PPM} ppm NIOSH REL (acute, screen)`,
      );
    }
  }
  // VOC (CARCINOGEN): total VOC peak over the screening level.
  const voc = num(record.voc_ppm_peak);
  if (voc !== null && voc >= VOC_SCREEN_PPM) {
    vocOver = true;
    reasons.push(
      `VOC peak ${voc} ppm ≥ ${VOC_SCREEN_PPM} ppm total-VOC screen (carcinogen)`,
    );
  }
  // H₂S / LEL (ACUTE): peak vs ceiling / action level — both instantaneous limits.
  const h2s = num(record.h2s_ppm_peak);
  if (h2s !== null && h2s >= H2S_CEILING_PPM) {
    h2sLelOver = true;
    reasons.push(`H₂S peak ${h2s} ppm ≥ ${H2S_CEILING_PPM} ppm ceiling`);
  }
  const lel = num(record.lel_pct_peak);
  if (lel !== null && lel >= LEL_ACTION_PCT) {
    h2sLelOver = true;
    reasons.push(`LEL peak ${lel}% ≥ ${LEL_ACTION_PCT}% action level`);
  }

  const carcinogenOver = vocOver; // total VOC is the cancer signal
  const acuteOver = coOver || h2sLelOver; // CO + H₂S + LEL are immediate danger
  return {
    over: carcinogenOver || acuteOver,
    carcinogenOver,
    acuteOver,
    coOver,
    vocOver,
    reasons,
  };
}
